import { Button, Chip, Heading, Link } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { CheckCircleIcon } from '@mm/icons';
import twilioCRUDActions from '@mm/state/actions/TwilioCRUDActions';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import { UserRole } from '@mm/state/model/CompanyUsersModel';
import modalActions, {
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { companyFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectInstagramAccounts } from '@mm/state/selectors/instagramSelectors';
import { getCurrentSubscription } from '@mm/state/selectors/subscriptionSelectors';
import {
  hasDeactivatedNumberSelector,
  recentDiscardedSelector,
} from '@mm/state/selectors/twillio';
import {
  hasConnectedRealFacebookPageSelector,
  selectCurrentUser,
} from '@mm/state/selectors/userSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import classNames from 'classnames';
import React, { useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { GreenMarker } from '../../../../../../common/components/GreenMarker/GreenMarker';
import { RequiredPlanBadge } from '../../../../../../common/components/ProBadge';
import { trackWebAppGAEvent } from '../../../../../../common/helpers/plugins/website-google-analytics';
import { useGreenCueMarkers } from '../../../../../../common/hooks/use-green-cue-markers';
import { usePhoneNumber } from '../../../../../../common/hooks/use-phone-number';
import { IG_BETA_SIGNUP_GA_EVENT } from '../../../../../../common/modals/InstagramConnectModal/InstagramConnectModal';
import { FacebookLoginAndConnectButton } from '../../../../../../modules/permissions/ConnectPageWarning/ConnectPageWarning';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { ImportContactsButton } from '../../../../contacts/ImportContactsButton';
import { ChannelIcon } from '../../../../liveChat/components/ChannelIcon';
import { getColorStylesByType } from '../../../styles/colorsByType';
import { useCreateChatStarter } from '../../ChatStarters/use-create-chat-starter';
import './Channels.pcss';

type Props = {
  className?: string;
};

const PlusOneChip = () => {
  const facebookPage = useSelector(activeFacebookPageSelector);

  const currentSubscription = useSelector(
    getCurrentSubscription(facebookPage?.id),
  );

  if (
    currentSubscription ||
    !!facebookPage?.subscription ||
    !facebookPage?.redemption?.freeTrial
  ) {
    return null;
  }
  return <Chip className="channels__plus-chip" text="+1 Day" type="success" />;
};

export const Channels = ({ className }: Props) => {
  const facebookPage = useSelector(activeFacebookPageSelector);
  const currentUser = useSelector(selectCurrentUser);
  const hasDeactivatedNumber = useSelector(hasDeactivatedNumberSelector);
  const recentDiscardedNumber = useSelector(recentDiscardedSelector);
  const isFreePlan = !facebookPage?.subscription;
  const isCanceledPlan =
    facebookPage?.subscription?.canceledAt &&
    facebookPage?.subscription?.state !== 'active';

  const chatbotId = useSelector(activeChatbotIdSelector);
  const featureFlags = useSelector(companyFeatureFlagsSelector);

  const isUserPageConnected = useSelector(hasConnectedRealFacebookPageSelector);

  const [connectError, setConnectError] = useState(false);

  const history = useHistory();
  const dispatch = useDispatch();

  const { isSmsSupported, phoneNumber } = usePhoneNumber();
  const instagramAccounts = useSelector(selectInstagramAccounts);

  const [isPhoneCopied, setIsPhoneCopied] = useState<boolean>();
  const { isSmsActivated, isWebChatActivated } = useGreenCueMarkers();

  const { openInstagramConnectModal, openChatbotLimitModal } =
    useModalActions();

  const fb = (
    <div className="channels__channel" key="fb">
      <div className="channels__channel-name">
        <div className="channels__channel-icon">
          <ChannelIcon
            className="channels__channel-icon-svg"
            channel="facebook"
          />
        </div>
        <Heading size={5}>Facebook</Heading>
      </div>

      {isUserPageConnected || facebookPage?.realFacebookPage ? (
        <>
          <div className="channels__description">
            {'FB ID: '}
            <Link
              href={`https://www.facebook.com/${facebookPage?.remoteId}`}
              isBlank
            >
              {facebookPage?.remoteId}
            </Link>
          </div>

          <div
            className="channels__action"
            onClick={() =>
              history.push(
                BotBuilderRoutes.facebookPageWelcomer.getLink({ chatbotId }),
              )
            }
          >
            Edit Welcomer
          </div>
        </>
      ) : (
        <>
          <div className="channels__description">Connect a Facebook Page</div>
          <FacebookLoginAndConnectButton
            destinationPageId={facebookPage?.id}
            className="channels__connect-btn"
          />
        </>
      )}
    </div>
  );

  const { createChatStarter } = useCreateChatStarter();

  const webchat = (
    <div className="channels__channel" key="webchat">
      <div className="channels__channel-name">
        <div className="channels__channel-icon">
          <ChannelIcon
            className="channels__channel-icon-svg"
            channel="webchat"
          />
        </div>
        <Heading size={5}>Website Chat</Heading>
      </div>

      {facebookPage?.webChatActivated ? (
        <>
          <div
            className="channels__action"
            onClick={() => {
              history.push(
                BotBuilderRoutes.chatStarters.getLink({
                  chatbotId,
                  kind: 'customer_chat_widget',
                }),
              );
            }}
          >
            Edit website chat widget
          </div>
        </>
      ) : (
        <>
          <div
            className="channels__action"
            onClick={() => createChatStarter('customer_chat_widget')}
          >
            {!isWebChatActivated && (
              <GreenMarker className="channels__action--green-marker" />
            )}
            Install chat on your website
          </div>
        </>
      )}
    </div>
  );

  const onActivateSMS = async () => {
    if (
      hasDeactivatedNumber &&
      facebookPage &&
      !isFreePlan &&
      !isCanceledPlan
    ) {
      await dispatch(
        twilioCRUDActions.activatePage(facebookPage.id, true, {
          numberKind: 'twilio',
          number: recentDiscardedNumber,
        }),
      );
    } else if (hasDeactivatedNumber && isFreePlan) {
      dispatch(
        modalActions.openUniversalUpgradeLandmineModal({
          feature: 'smsActivateNumber',
          landmineName: landmineNames['SMS-ACTIVATE-NUMBER'],
        }),
      );
    } else {
      history.push(BotBuilderRoutes.settingsSmsActivate.getLink({ chatbotId }));
    }
  };

  const sms = (
    <div className="channels__channel" key="sms">
      <div className="channels__channel-name">
        <div className="channels__channel-icon">
          <ChannelIcon className="channels__channel-icon-svg" channel="sms" />
        </div>
        <Heading size={5}>SMS</Heading>
        <RequiredPlanBadge billingType={PageBillingType.Pro} />
      </div>

      {phoneNumber ? (
        <>
          <div className="channels__description">
            {isPhoneCopied ? (
              <span className="channels__success-info">
                <CheckCircleIcon
                  className="channels__success-icon"
                  color={getColorStylesByType('success').color}
                />
                Copied!
              </span>
            ) : (
              <>
                Copy Phone number :{' '}
                <CopyToClipboard text={phoneNumber.number}>
                  <Link
                    href={`tel:${phoneNumber.number}`}
                    onClick={e => {
                      e.preventDefault();
                      setIsPhoneCopied(true);
                    }}
                  >
                    {phoneNumber.number}
                  </Link>
                </CopyToClipboard>
              </>
            )}
          </div>

          <div className="channels__action">
            <ImportContactsButton
              text="Upload contacts"
              isSecondary
              asDiv
              className="channels__action__import-contacts-btn"
            />
          </div>
        </>
      ) : (
        <>
          <div className="channels__action" onClick={onActivateSMS}>
            {hasDeactivatedNumber
              ? `Restore ${recentDiscardedNumber}`
              : 'Activate an SMS number'}
            {!isSmsActivated && (
              <GreenMarker className="channels__action--green-marker" />
            )}
          </div>
        </>
      )}
    </div>
  );

  const onInstagramConnectError = () => {
    setConnectError(true);
  };

  const connectInstagram = async () => {
    if (
      currentUser &&
      (currentUser.role === UserRole.Owner ||
        currentUser.role === UserRole.Admin)
    ) {
      trackWebAppGAEvent('click', {
        category: IG_BETA_SIGNUP_GA_EVENT,
        label: `1_apply_click__owner_or_admin`,
      });

      return openInstagramConnectModal({
        onInstagramConnectError,
      });
    }

    trackWebAppGAEvent('click', {
      category: IG_BETA_SIGNUP_GA_EVENT,
      label: `1_apply_click__no_access`,
    });
    return openChatbotLimitModal(
      'Only account owner and admins can connect an Instagram account.',
      'Admin access needed',
    );
  };

  const instagramAccountId = instagramAccounts[0]?.instagramId;

  const instagramUsername =
    facebookPage?.availableInstagramAccountsData?.username;

  const instagram = (
    <div className="channels__channel" key="instagram">
      <div className="channels__channel-name">
        <div className="channels__channel-icon">
          <ChannelIcon
            className="channels__channel-icon-svg"
            channel="instagram"
          />
        </div>
        <Heading size={5}>Instagram</Heading>
      </div>

      {isUserPageConnected || facebookPage?.realFacebookPage ? (
        !facebookPage?.instagramActivated ? (
          <>
            <div className="channels__action" onClick={connectInstagram}>
              Try Instagram Autoresponders
            </div>
          </>
        ) : (
          <>
            {instagramAccountId && (
              <>
                <div className="channels__description">
                  {instagramUsername ? 'Account: ' : 'Account ID: '}
                  {instagramUsername ? (
                    <div className="channels__instagram-name">
                      <Link
                        href={`https://www.instagram.com/${instagramUsername}`}
                        isBlank
                        capitalize={false}
                      >
                        @{instagramUsername}
                      </Link>
                    </div>
                  ) : (
                    <div className="channels__instagram-name">
                      {instagramAccountId}
                    </div>
                  )}
                </div>
                {featureFlags.instachamp && (
                  <Button
                    css={{ container: 'channels__edit-with-instachamp' }}
                    color="purple"
                    onClick={() => {
                      window.location.href = `/instachamp/@${instagramUsername}`;
                    }}
                  >
                    Edit with InstaChamp
                  </Button>
                )}
              </>
            )}
          </>
        )
      ) : (
        <>
          <div className="channels__description">Connect a Facebook Page</div>
          <FacebookLoginAndConnectButton
            destinationPageId={facebookPage?.id}
            className="channels__connect-btn"
          />
        </>
      )}
    </div>
  );

  const channels = [fb, webchat, isSmsSupported ? sms : undefined, instagram];
  const styles = {
    gridTemplateColumns: '1fr '.repeat(channels.filter(Boolean).length),
  };

  return (
    <div className="channels-block">
      <Heading size={4} className="channels-block__header">
        Create more engagements now
      </Heading>
      <div className={classNames(className, 'channels')} style={styles}>
        {channels}
      </div>
    </div>
  );
};
